import React, { useEffect, useState } from "react";
import { api, PLATFORM_LABELS, PLATFORM_COLORS, fmtEuro, fmtNum } from "@/lib/api";
import { ChartTooltip } from "@/components/ChartTooltip";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Loader2 } from "lucide-react";

export default function Statistiques() {
  const [platforms, setPlatforms] = useState([]);
  const [evo, setEvo] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [p, e, m, s] = await Promise.all([
        api.get("/stats/platforms"),
        api.get("/stats/evolution?days=60"),
        api.get("/stats/monthly?months=12"),
        api.get("/dashboard/stats"),
      ]);
      setPlatforms(p.data);
      setEvo(e.data);
      setMonthly(m.data);
      setStats(s.data);
      setLoading(false);
    })();
  }, []);

  if (loading || !stats) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const avgDistance = stats.courses_month > 0 ? stats.km_month / stats.courses_month : 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Analyses détaillées</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Statistiques</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Panier moyen" value={fmtEuro(stats.ca_per_course)} testid="stat-panier-moyen" />
        <MiniStat label="Distance moyenne" value={`${fmtNum(avgDistance, 1)} km`} testid="stat-distance-moy" />
        <MiniStat label="CA / heure" value={fmtEuro(stats.ca_per_hour)} testid="stat-ca-heure" />
        <MiniStat label="Temps travaillé (mois)" value={`${fmtNum(stats.hours_month, 1)} h`} testid="stat-hours-mois" />
      </div>

      {/* Platform rentabilité */}
      <div className="card-surface p-5" data-testid="table-platforms">
        <h3 className="font-manrope font-semibold mb-4">Rentabilité par plateforme (mois)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-2 py-2">Plateforme</th>
                <th className="text-right px-2 py-2">Courses</th>
                <th className="text-right px-2 py-2">Heures</th>
                <th className="text-right px-2 py-2">Km</th>
                <th className="text-right px-2 py-2">CA net</th>
                <th className="text-right px-2 py-2">€ / course</th>
                <th className="text-right px-2 py-2">€ / heure</th>
                <th className="text-right px-2 py-2">Commission</th>
              </tr>
            </thead>
            <tbody>
              {platforms.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-4 text-muted-foreground">Aucune donnée ce mois</td></tr>
              ) : platforms.map((p) => (
                <tr key={p.platform} className="border-b border-border/60">
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: PLATFORM_COLORS[p.platform] }} />
                      {PLATFORM_LABELS[p.platform] || p.platform}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right num">{p.count}</td>
                  <td className="px-2 py-2 text-right num">{fmtNum(p.hours, 1)}</td>
                  <td className="px-2 py-2 text-right num">{fmtNum(p.km, 1)}</td>
                  <td className="px-2 py-2 text-right num font-semibold">{fmtEuro(p.ca)}</td>
                  <td className="px-2 py-2 text-right num">{fmtEuro(p.avg_per_course)}</td>
                  <td className="px-2 py-2 text-right num text-primary">{fmtEuro(p.avg_per_hour)}</td>
                  <td className="px-2 py-2 text-right num text-destructive">{fmtEuro(p.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-surface p-4" data-testid="chart-stats-evo">
          <h3 className="font-manrope font-semibold mb-4">Évolution CA & Bénéfice (60 j)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evo}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="ca" name="CA" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="benefice" name="Bénéfice" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-4" data-testid="chart-stats-monthly">
          <h3 className="font-manrope font-semibold mb-4">12 derniers mois</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ca" name="CA" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="depenses" name="Dépenses" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-4 lg:col-span-2" data-testid="chart-stats-km">
          <h3 className="font-manrope font-semibold mb-4">Kilométrage mensuel</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip valueFormatter={(v) => `${fmtNum(v, 1)} km`} />} />
                <Bar dataKey="km" name="Km" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className="font-manrope font-extrabold text-2xl num">{value}</div>
    </div>
  );
}
